import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  type EquipmentAlias,
  useCreateManufacturerMutation,
  useGetManufacturerQuery,
  useUpdateManufacturerMutation,
} from '@/services/equipmentManufacturers';
import { isErrorWithData } from '@/services/helpers';
import { PalmettoButton, PalmettoComboBox, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorCode, ProgramType } from 'types';

import { AliasEditor, normalizeAliases } from '../approved-vendor-list/AliasEditor';

import { EquipmentBreadcrumb } from './EquipmentBreadcrumb';
import { programTypeOptions } from './equipmentLabels';

interface ManufacturerFormValues {
  name: string;
  programTypes: ProgramType[];
  aliases: EquipmentAlias[];
  note: string;
}

const emptyValues: ManufacturerFormValues = {
  name: '',
  programTypes: [ProgramType.hvac],
  aliases: [{ source: '', value: '' }],
  note: '',
};

export const EquipmentManufacturerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: existing, isLoading: isLoadingExisting } = useGetManufacturerQuery(id ?? skipToken);
  const [createManufacturer, { isLoading: isCreating }] = useCreateManufacturerMutation();
  const [updateManufacturer, { isLoading: isUpdating }] = useUpdateManufacturerMutation();

  const prefillValues = useMemo<ManufacturerFormValues | undefined>(
    () =>
      existing
        ? {
            name: existing.name,
            programTypes: existing.programTypes ?? [],
            aliases: existing.aliases?.length ? existing.aliases : [{ source: '', value: '' }],
            note: '',
          }
        : undefined,
    [existing],
  );

  const { control, handleSubmit, setError } = useForm<ManufacturerFormValues>({
    defaultValues: emptyValues,
    values: prefillValues,
  });

  const isLoading = isCreating || isUpdating || isLoadingExisting;

  const onSubmit = useCallback(
    async (values: ManufacturerFormValues) => {
      if (values.programTypes.length === 0) {
        setError('programTypes', { type: 'server', message: 'At least one program type is required' });
        return;
      }

      const payload = {
        name: values.name.trim(),
        programTypes: values.programTypes,
        aliases: normalizeAliases(values.aliases),
      };

      try {
        const saved = isEdit
          ? await updateManufacturer({
              id: id!,
              data: { ...payload, ...(values.note.trim() ? { note: values.note.trim() } : {}) },
            }).unwrap()
          : await createManufacturer(payload).unwrap();

        toast.success(isEdit ? 'Manufacturer updated' : 'Manufacturer created');
        navigate(`/admin/approved-vendors/manufacturers/${saved.id}`);
      } catch (error) {
        console.error('Error saving equipment manufacturer:', error);
        if (!isErrorWithData(error)) {
          toast.error('Failed to save manufacturer. Please try again.');
          return;
        }

        // The server names the conflicting field through a typed code, so the message lands on the
        // offending input rather than a toast the user has to map back to a field. Its wording is
        // passed through as sent.
        const { errorCode, message } = error.data;
        if (errorCode === ErrorCode.equipmentDuplicateName) {
          setError('name', { type: 'server', message });
        } else if (errorCode === ErrorCode.equipmentDuplicateAlias) {
          setError('aliases', { type: 'server', message });
        } else {
          toast.error(message);
        }
      }
    },
    [createManufacturer, updateManufacturer, isEdit, id, navigate, setError],
  );

  const handleCancel = useCallback(
    () => navigate(isEdit ? `/admin/approved-vendors/manufacturers/${id}` : '/admin/approved-vendors/manufacturers'),
    [navigate, isEdit, id],
  );

  const pageTitle = isEdit ? `Edit: ${existing?.name ?? 'Loading...'}` : 'New Manufacturer';

  return (
    <DsmPageContainer title={pageTitle}>
      <PageHeader title={pageTitle} eyebrow={<EquipmentBreadcrumb current={isEdit ? existing?.name : 'New'} />} />

      <form onSubmit={handleSubmit(onSubmit)} className="mx-6">
        <div className="mb-16 grid grid-cols-2 gap-x-16 gap-y-8">
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id="manufacturer-name"
                label="Name"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                validationMessage={
                  fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                }
                disabled={isLoading}
              />
            )}
          />
          <Controller
            name="programTypes"
            control={control}
            render={({ field, fieldState }) => (
              <PalmettoComboBox
                id="manufacturer-program-types"
                label="Program Types"
                items={programTypeOptions.map((opt) => ({
                  ...opt,
                  selected: field.value.includes(opt.value),
                }))}
                onValueChange={(details: { value: string[] }) => field.onChange(details.value)}
                validationMessage={
                  fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                }
                disabled={isLoading}
              />
            )}
          />
        </div>

        <div className="mb-16">
          <Controller
            name="aliases"
            control={control}
            render={({ field, fieldState }) => (
              <AliasEditor
                value={field.value}
                onChange={field.onChange}
                disabled={isLoading}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>

        {isEdit && (
          <div className="mb-16">
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <PalmettoTextInput
                  id="manufacturer-note"
                  label="Change Note"
                  helperText="Optional note describing this change (recorded in history)"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={isLoading}
                />
              )}
            />
          </div>
        )}

        <div className="flex justify-end gap-8">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </PalmettoButton>
          <PalmettoButton emphasis="primary" layout="hug" disabled={isLoading}>
            {isEdit ? 'Save Changes' : 'Create Manufacturer'}
          </PalmettoButton>
        </div>
      </form>
    </DsmPageContainer>
  );
};
